import i18next from 'i18next'
import { RefinementCtx, z } from 'zod'

import { WorkspacePrebidModuleSlice } from 'features/globalSetup/contexts/PrebidModulesContext'
import { validateJSONSchema } from 'utils/json'
import { WorkspaceBidderSlice } from '../GeneralPaapiForm/useGetWorkspaceBidders'
import { mapBidderIdsToCodes } from '../GeneralPaapiForm/utils'

export const paapiParamsSchema = z.object({
  componentSeller: z.string().superRefine((value: string | undefined, ctx: RefinementCtx) => {
    if (value != undefined && value.length > 0) {
      return validateJSONSchema(value, ctx)
    }
  }),
  defaultForSlots: z.number(),
  id: z.string(),
  paapiForGpt: z.boolean(),
  parallel: z.boolean(),
})
export type EditPaapiParamsSchema = z.infer<typeof paapiParamsSchema>

const workspaceBidder = z.object({
  bidderCode: z.string(),
  id: z.string(),
})
export type WorkspaceBidder = z.infer<typeof workspaceBidder>

const metaSchema = z.object({
  workspaceBidders: z.array(workspaceBidder),
})

export const editPaapiSchema = z
  .object({
    bidders: z.array(z.string()),
    enabled: z.boolean(),
    meta: metaSchema,
  })
  .extend(paapiParamsSchema.shape)
  .superRefine(({ bidders, meta }, context) => {
    if (
      bidders.length > 0 &&
      bidders.some(
        (bidder: WorkspaceBidder['bidderCode']): boolean =>
          meta.workspaceBidders.find(
            (workspaceBidder: WorkspaceBidder) => workspaceBidder.bidderCode === bidder,
          ) === undefined,
      )
    ) {
      context.addIssue({
        code: 'custom',
        message: i18next.t('common:form.error.outOfRange'),
        path: ['bidders'],
      })
    }
  })

export type EditPaapiSchema = z.infer<typeof editPaapiSchema>

export const editPaapiSchemaDefaults = (
  workspacePrebidModule: WorkspacePrebidModuleSlice,
  workspaceBidders: WorkspaceBidderSlice[],
): EditPaapiSchema => {
  const { enabled, id, parameters, workspaceBidderIds } = workspacePrebidModule

  const { componentSeller, defaultForSlots, paapiForGpt, parallel } =
    parameters as EditPaapiParamsSchema

  return {
    bidders: mapBidderIdsToCodes(workspaceBidderIds, workspaceBidders),
    componentSeller,
    defaultForSlots,
    enabled,
    id,
    meta: { workspaceBidders },
    paapiForGpt,
    parallel,
  }
}
